/**
 * OfflineContext - Contexte React pour le mode hors-ligne
 *
 * Fournit l'état et les actions du mode offline à toute l'application.
 * Utilise useOfflineMode en interne.
 */
import { createContext, useContext, ReactNode } from 'react';
import { useOfflineMode, UseOfflineModeReturn } from '../hooks/useOfflineMode';

// ============================================================================
// CONTEXTE
// ============================================================================

const OfflineContext = createContext<UseOfflineModeReturn | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

interface OfflineProviderProps {
  children: ReactNode;
}

export function OfflineProvider({ children }: OfflineProviderProps) {
  const offlineMode = useOfflineMode();

  return (
    <OfflineContext.Provider value={offlineMode}>
      {children}
    </OfflineContext.Provider>
  );
}

// ============================================================================
// HOOK D'ACCÈS
// ============================================================================

export function useOffline(): UseOfflineModeReturn {
  const context = useContext(OfflineContext);

  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }

  return context;
}

// ============================================================================
// HOOKS DÉRIVÉS (CONVENIENCES)
// ============================================================================

/**
 * Hook simple pour vérifier si on est online
 */
export function useIsOnline(): boolean {
  const { isOnline } = useOffline();
  return isOnline;
}

/**
 * Hook pour le nombre de ventes en attente
 */
export function usePendingSalesCount(): number {
  const { pendingSalesCount } = useOffline();
  return pendingSalesCount;
}

/**
 * Hook pour vérifier s'il y a des problèmes de sync
 */
export function useHasSyncIssues(): boolean {
  const { conflictSalesCount, errorSalesCount } = useOffline();
  return conflictSalesCount > 0 || errorSalesCount > 0;
}
